import { useCallback, useEffect, useRef, useState } from 'react'
import { DateUtils } from 'src/utils/date'
import { Dayjs } from 'dayjs'

type useCountdownProps = {
  countExpiredCallbackFn: () => void
}

type CountdownControllers = {
  count: string
  /** 인증 등 남은 시간이 0보다 클 때 true (만료 직전·진행 중) */
  countDownActive: boolean
  countStart: (timeout: number) => void
  countStop: () => void
}

export const useCountdown = ({
  countExpiredCallbackFn,
}: useCountdownProps): CountdownControllers => {
  const { now, getDayDifference, timeFormatter } = DateUtils
  const [count, setCount] = useState<number>(0)
  const [endTime, setEndTime] = useState<Dayjs | null>(null)

  const countStart = useCallback(
    (timeout: number) => {
      setCount(timeout)
      setEndTime(now().add(timeout * 1000, 'milliseconds'))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [count],
  )
  const countStop = () => {
    setEndTime(null)
    setCount(0)
  }

  useInterval(
    useCallback(() => {
      if (count < 1) {
        countExpiredCallbackFn?.()
        countStop()
        return
      }
      setCount(Math.ceil(Number(getDayDifference(endTime!, 'milliseconds')) / 1000))
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count, countStop]),
    endTime ? 1000 : null,
  )

  return {
    count: timeFormatter(count),
    countDownActive: endTime !== null && count > 0,
    countStop,
    countStart,
  }
}

function useInterval(callback: () => void, delay: number | null) {
  const savedCallback = useRef(callback)

  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  useEffect(() => {
    if (delay === null) {
      return
    }

    const id = setInterval(() => {
      savedCallback.current()
    }, delay)

    return () => {
      clearInterval(id)
    }
  }, [delay])
}
